import { Message, sendMessage } from '../Communication';
import { wsApi } from './WebSocketWrapper';

import { rejectValue } from '@egr/xbox/utils/Promise';
import { getEnvBoolean } from '@egr/xbox/utils/ReactScriptHelper';

const ALLOW_MIXED_API_REQUESTS: boolean = getEnvBoolean('DEBUG_ALLOW_MIXED_API_REQUESTS', true);

/** @internal */
export interface DescriptorAction {
    descriptor: string;
}

/** @internal */
export interface PathActions {
    path: string;
}

/** @internal */
export interface CopyAction {
    source: string;
    destination: string;
    override: boolean;
}

export type OpenMode = 'r' | 'w' | 'a';
export type Converter = '' | 'base64';

/** @internal */
export interface OpenAction extends PathActions {
    mode: OpenMode;
    converter: Converter;
}

/** @internal */
export interface ReadAction extends DescriptorAction {
    size: number;
}

/** @internal */
export interface WriteAction extends DescriptorAction {
    data: string;
}

export function close(descriptor: string): Promise<void> {
    if (wsApi != null) { // Note: never allow mixed api requests for the close action
        throw new Error('api method not supported: close');
    }

    return sendMessage<Message<DescriptorAction>>({
        type: 'fs',
        action: 'close',
        arguments: {
            descriptor: descriptor
        }
    });
}

export function copy(src: string, dst: string, override: boolean): Promise<void> {
    if (wsApi != null) {
        return wsApi.copy(src, dst, override);
    }

    return sendMessage<Message<CopyAction>>({
        type: 'fs',
        action: 'copy',
        arguments: {
            source: src,
            destination: dst,
            override: override
        }
    });
}

export async function move(src: string, dst: string): Promise<void> {
    // FixMe: define app api!
    await copy(src, dst, true);
    await remove(src);
}

export function flush(descriptor: string): Promise<void> {
    if (wsApi != null) { // Note: never allow mixed api requests for the flush action
        throw new Error('api method not supported: flush');
    }

    return sendMessage<Message<DescriptorAction>>({
        type: 'fs',
        action: 'flush',
        arguments: {
            descriptor: descriptor
        }
    });
}

export function list(path: string): Promise<Array<string>> {
    if (wsApi != null) {
        return rejectValue(wsApi.list(path), []);
    }

    return sendMessage<Message<PathActions>, Array<string>>({
        type: 'fs',
        action: 'list',
        arguments: {
            path: path
        }
    });
}

export function open(path: string, mode: OpenMode, converter: Converter): Promise<string> {
    if (wsApi != null) { // Note: never allow mixed api requests for the open action
        throw new Error('api method not supported: open');
    }

    return sendMessage<Message<OpenAction>, string>({
        type: 'fs',
        action: 'open',
        arguments: {
            path: path,
            mode: mode,
            converter: converter
        }
    });
}

export function read(descriptor: string, length: number): Promise<string> {
    if (wsApi != null) { // Note: never allow mixed api requests for the read action
        throw new Error('api method not supported: read');
    }

    return sendMessage<Message<ReadAction>, string>({
        type: 'fs',
        action: 'read',
        arguments: {
            descriptor: descriptor,
            size: length
        }
    });
}

export function remove(path: string): Promise<void> {
    if (wsApi != null) {
        return wsApi.remove([path]);
    }

    return sendMessage<Message<PathActions>>({
        type: 'fs',
        action: 'remove',
        arguments: {
            path: path
        }
    });
}

export function size(path: string): Promise<number> {
    if (wsApi != null) {
        return wsApi.size(path);
    }

    return sendMessage<Message<PathActions>, number>({
        type: 'fs',
        action: 'size',
        arguments: {
            path: path
        }
    });
}

export function write(descriptor: string, data: string): Promise<void> {
    if (wsApi != null) { // Note: never allow mixed api requests for the write action
        throw new Error('api method not supported: write');
    }

    return sendMessage<Message<WriteAction>>({
        type: 'fs',
        action: 'write',
        arguments: {
            descriptor: descriptor,
            data: data
        }
    });
}

// opens specified Folder (UWP only)
export function showFolder(path: string): Promise<void> {
    return sendMessage<Message<PathActions>>({
        type: 'fs',
        action: 'show_fs_root',
        arguments: {
            path: path
        }
    });
}

/** @internal */
export interface ZipAction {
    src: string;
    target: string;
    // not a valid property for the native app
    _debugFsOnly_ignoreAndLogErrors?: boolean;
}

/** @internal */
export type UnzipAction = ZipAction;

export function zip(target: string, src: string, _debugFsOnly_ignoreAndLogErrors?: boolean): Promise<void> {
    if (wsApi != null && ALLOW_MIXED_API_REQUESTS === false) {
        throw new Error('api method not supported: zip');
    }

    return sendMessage<Message<ZipAction>>({
        type: 'fs',
        action: 'zip',
        arguments: {
            src,
            target,
            _debugFsOnly_ignoreAndLogErrors
        }
    });
}

export function unzip(src: string, target: string): Promise<void> {
    if (wsApi != null && ALLOW_MIXED_API_REQUESTS === false) {
        throw new Error('api method not supported: unzip');
    }

    return sendMessage<Message<UnzipAction>>({
        type: 'fs',
        action: 'zip',
        arguments: {
            src,
            target
        }
    });
}

export interface HashFileInfo {
    size: number;
    hash: string;
}
export function hash(paths: Array<string>, hashType: 'md5' = 'md5'): Promise<Array<HashFileInfo>> {
    if (wsApi != null && ALLOW_MIXED_API_REQUESTS === false) {
        throw new Error('api method not supported: hash');
    }

    return sendMessage<Message<{type: string, paths: Array<string>}>, Array<HashFileInfo>>({
        type: 'fs',
        action: 'hash',
        arguments: {
            type: hashType,
            paths
        }
    });
}